<script>
    import { createEventDispatcher } from "svelte";
    import { Section } from "../../lib/ui";
    import ImagePicker from "../chat-builder/ImagePicker.svelte";
    import { botAssetFolder } from "../chat-builder/imageAssetApi.js";
    import { ASSET_GROUPS, ENEMY_KINDS, ASSET_SET_STEPS } from "./gameSlots.js";
    import AssetSetBatch from "./AssetSetBatch.svelte";
    import PlayFieldPreview from "./PlayFieldPreview.svelte";

    export let bot;
    /** 슬롯키 → assetId(ref). 저장되는 값. */
    export let assets = {};
    /** 슬롯키 → 현재 버전 URL. 썸네일 표시용(저장하지 않는다). */
    export let urls = {};

    const dispatch = createEventDispatcher();

    let pickerOpen = false;
    let pickerSlot = null;

    const openPicker = (slotKey) => {
        pickerSlot = slotKey;
        pickerOpen = true;
    };

    const onPick = (e) => {
        const { assetId, url } = e.detail;
        pickerOpen = false;
        if (!pickerSlot) return;
        dispatch("change", {
            assets: { ...assets, [pickerSlot]: assetId },
            urls: { ...urls, [pickerSlot]: url },
        });
        pickerSlot = null;
    };

    // 슬롯을 비우면 런타임이 내장 기본으로 돌아간다 — 값을 지우는 게 곧 "기본으로"다.
    const clearSlot = (slotKey) => {
        const nextAssets = { ...assets };
        const nextUrls = { ...urls };
        delete nextAssets[slotKey];
        delete nextUrls[slotKey];
        dispatch("change", { assets: nextAssets, urls: nextUrls });
    };

    // 일괄 생성이 한 장 끝낼 때마다 그 슬롯을 채운다(중간에 끊겨도 남게).
    const onBatchGenerated = (e) => {
        const { slot, assetId, url } = e.detail;
        dispatch("change", {
            assets: { ...assets, [slot]: assetId },
            urls: { ...urls, [slot]: url },
        });
    };

    const basicGroup = ASSET_GROUPS.find((g) => !g.enemyStates);
    const enemyGroup = ASSET_GROUPS.find((g) => g.enemyStates);
</script>

<AssetSetBatch
    chatId={bot.id}
    steps={ASSET_SET_STEPS}
    folderPath={botAssetFolder(bot)}
    scopeLabel="이 게임의 배경·코인·적"
    on:generated={onBatchGenerated}
/>

<Section
    title="플레이 화면 미리보기"
    description="아래에서 고른 그림이 게임에서 실제로 이렇게 보입니다. (오른쪽 프리뷰는 메인 화면이라 이 그림들이 안 나옵니다)"
>
    <PlayFieldPreview {urls} />
</Section>

<Section title={basicGroup.title} description={basicGroup.desc}>
    <div class="tw-flex tw-flex-wrap tw-gap-4">
        {#each basicGroup.slots as slot (slot.key)}
            <div class="tw-flex tw-w-[108px] tw-flex-col tw-items-center tw-gap-1">
                <button
                    type="button"
                    class="slot-thumb {urls[slot.key]
                        ? 'tw-border-border'
                        : 'tw-border-dashed tw-border-border tw-bg-muted'}"
                    on:click={() => openPicker(slot.key)}
                >
                    {#if urls[slot.key]}
                        <img crossorigin="anonymous" class="tw-max-h-full tw-max-w-full tw-object-contain" src={urls[slot.key]} alt="" />
                    {:else}
                        <span class="tw-text-2xl tw-text-muted-foreground">+</span>
                    {/if}
                </button>
                <div class="tw-text-[13px] tw-font-semibold tw-text-foreground">{slot.label}</div>
                {#if slot.hint}
                    <div class="tw-text-center tw-text-[11px] tw-leading-tight tw-text-muted-foreground">{slot.hint}</div>
                {/if}
                {#if urls[slot.key]}
                    <button
                        type="button"
                        class="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-text-[11px] tw-text-muted-foreground tw-underline"
                        on:click={() => clearSlot(slot.key)}>기본으로</button
                    >
                {/if}
            </div>
        {/each}
    </div>
</Section>

<Section title={enemyGroup.title} description={enemyGroup.desc}>
    <!-- 적은 "한 종류 = 한 줄(기본/맞을 때/죽을 때)"로 묶어 보여준다.
         슬롯을 평평하게 늘어놓으면 어떤 상태가 어떤 적의 것인지 읽히지 않는다. -->
    {#each ENEMY_KINDS as kind, i (kind.slot)}
        <div
            class="tw-flex tw-flex-col tw-gap-4 tw-py-4 sm:tw-flex-row sm:tw-gap-5 {i > 0
                ? 'tw-border-t tw-border-border'
                : 'tw-pt-0'}"
        >
            <div class="tw-flex tw-w-[120px] tw-shrink-0 tw-flex-col tw-gap-0.5 sm:tw-pt-2">
                <span class="tw-h-2.5 tw-w-2.5 tw-rounded-full" style="background:{kind.color}"></span>
                <span class="tw-text-sm tw-font-bold tw-text-foreground">{kind.label}</span>
                <span class="tw-text-xs tw-text-muted-foreground">{kind.hint}</span>
            </div>
            <div class="tw-flex tw-flex-wrap tw-gap-4">
                {#each enemyGroup.slots.filter((s) => s.group === kind.slot) as slot (slot.key)}
                    <div class="tw-flex tw-w-[108px] tw-flex-col tw-items-center tw-gap-1">
                        <button
                            type="button"
                            class="slot-thumb {urls[slot.key]
                                ? 'tw-border-border'
                                : 'tw-border-dashed tw-border-border tw-bg-muted'}"
                            on:click={() => openPicker(slot.key)}
                        >
                            {#if urls[slot.key]}
                                <img crossorigin="anonymous" class="tw-max-h-full tw-max-w-full tw-object-contain" src={urls[slot.key]} alt="" />
                            {:else}
                                <span class="tw-text-2xl tw-text-muted-foreground">+</span>
                            {/if}
                        </button>
                        <div class="tw-text-[13px] tw-font-semibold tw-text-foreground">{slot.state}</div>
                        {#if slot.optional && !urls[slot.key]}
                            <div class="tw-text-center tw-text-[11px] tw-leading-tight tw-text-muted-foreground">
                                비우면 효과로 대신
                            </div>
                        {/if}
                        {#if urls[slot.key]}
                            <button
                                type="button"
                                class="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-text-[11px] tw-text-muted-foreground tw-underline"
                                on:click={() => clearSlot(slot.key)}>기본으로</button
                            >
                        {/if}
                    </div>
                {/each}
            </div>
        </div>
    {/each}
</Section>

<!-- 이미지 자산 라이브러리. 챗·허브가 쓰는 것과 같은 공용 모달이라 업로드·폴더·버전이 그대로 딸려온다.
     ⚠️ chatId 자리에 botId 를 넘긴다 — 이 API 의 파라미터 이름이 chatId 일 뿐 봇도 같은 자산 저장소를 쓴다
        (허브·기업형도 같은 방식). -->
<ImagePicker
    bind:open={pickerOpen}
    chatId={bot.id}
    contextFolder={botAssetFolder(bot)}
    mode="single"
    currentAssetId={pickerSlot ? assets[pickerSlot] : null}
    on:select={onPick}
/>

<style>
    /* 투명 PNG 가 대부분이라 밝은 바탕에선 안 보인다 — 게임 화면과 비슷한 어두운 바탕에 둔다.
       (토큰 색이 아니라 고정 색인 이유: 실제 게임 배경이 어둡기 때문. 테마와 무관하게 같아야 한다) */
    .slot-thumb {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 108px;
        height: 108px;
        padding: 0;
        border-width: 1px;
        border-style: solid;
        border-radius: 12px;
        background: #0d1430;
        cursor: pointer;
        overflow: hidden;
    }

    .slot-thumb:hover {
        border-color: hsl(var(--sb-primary));
    }
</style>
